import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  useTheme,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import InvoiceDetailsCard from './InvoiceDetailsCard';
import FileViewer from './FileViewer';
import InvoiceNotesTab from './InvoiceNotesTab';
import InvoiceActivityLogTab from './InvoiceActivityLogTab';
import InvoiceApprovalActions from './InvoiceApprovalActions';
import ApprovalPackageDetailsLayout from '../approvalPackages/approval-package-details-layout';
import InvoiceDetailsPopupStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceDetailsPopupStyles';
import useInvoiceDetailsPopup from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceDetailsPopup';
import useInvoiceFiles from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceFiles';
import { useApprovers } from '../../../hooks/purchaseInvoices/invoiceManagement/useApprovers';
import { useTransferApprover } from '../../../hooks/purchaseInvoices/invoiceManagement/useTransferApprover';

const InvoiceDetailsPopup = ({
  open,
  onClose,
  invoice,
  onSaveNotes,
  onApproveInvoice,
  onRejectInvoice,
  approvalPackageData,
  accountingFirm,
  fileAsBase64,
  isLoadingFile,
  initiateApproval,
  allocationKeyModalOpen,
  setAllocationKeyModalOpen,
  allocationKeys,
  isLoadingAllocationKeys,
  allocationKeysError,
  currentInvoiceYear,
  completeApprovalWithAllocationKey,
  updateInvoiceOptimistically,
  getAccountantDisplayName,
  accountantNamesCache
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const { 
    activeTab, 
    setActiveTab, 
    isProcessingApproval, 
    handleApproveInvoice, 
    handleRejectInvoice, 
    navigateToTab 
  } = useInvoiceDetailsPopup(open, onApproveInvoice, onRejectInvoice);
  
  const { approvers, isLoading: isLoadingApprovers } = useApprovers();
  const { transferApprover, isTransferring } = useTransferApprover();
  const {
    allFiles,
    currentFile,
    currentFileIndex,
    isLoadingFiles,
    totalFiles,
    hasNextFile,
    hasPreviousFile,
    goToNextFile,
    goToPreviousFile,
    goToFile,
    goToFileByName,
    refreshFiles
  } = useInvoiceFiles(invoice, fileAsBase64);

  const handleTransferApprover = async (transferData) => {
    await transferApprover(transferData);
  };

  const handleNavigateToSpecificFile = useCallback((fileName) => {
    const fileTabIndex = tabsConfig.findIndex(tab => tab.id === 'file');
    if (fileTabIndex !== -1) {
      setActiveTab(fileTabIndex);
    }

    setTimeout(() => {
      if (!allFiles || allFiles.length === 0) {
        enqueueSnackbar(`אין קבצים זמינים לחשבונית`, { variant: 'warning' });
        return;
      }
      
      console.log('Trying to find file:', fileName);
      console.log('Available files:', allFiles.map(file => ({
        name: file.name,
        originalFileName: file.originalFileName,
        displayName: file.displayName,
        type: file.type
      })));
      
      const success = goToFileByName(fileName);
      
      if (success) {
        enqueueSnackbar(`מציג קובץ: ${fileName}`, { variant: 'success' });
      } else {
        enqueueSnackbar(`לא נמצא קובץ בשם: ${fileName}`, { variant: 'warning' });
      }
    }, 250);
    
  }, [allFiles, goToFileByName, setActiveTab, enqueueSnackbar]);

  const renderInvoiceDetails = () => (
    <InvoiceDetailsCard
      invoice={invoice}
      approvalPackageData={approvalPackageData}
      onNavigateToFile={() => navigateToTab('file', tabsConfig)}
      onNavigateToComparison={() => navigateToTab('comparison', tabsConfig)}
      accountingFirm={accountingFirm}
      getAccountantDisplayName={getAccountantDisplayName}
      accountantNamesCache={accountantNamesCache}
    />
  );

const renderComparisonTab = () => {
  const isRetainerInvoice = invoice?.status === 'PENDING_RETAINER_ALLOCATION' || 
                            invoice?.status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
                            invoice?.isRetainerInvoice;
  
  return (
    <Box sx={InvoiceDetailsPopupStyles.comparisonBox}>
      {isRetainerInvoice && (
        <Box sx={InvoiceDetailsPopupStyles.retainerAlert}>
          <Box sx={InvoiceDetailsPopupStyles.retainerHeader}>
            <Box component="span" sx={InvoiceDetailsPopupStyles.retainerIcon}>
              💼
            </Box>
            <Typography sx={InvoiceDetailsPopupStyles.retainerTitle}>
              חשבונית ריטיינר
            </Typography>
          </Box>
          
          <Typography sx={InvoiceDetailsPopupStyles.retainerDescription}>
            זוהי חשבונית ריטיינר הממתינה לשיוך הזמנות נוספות.
          </Typography>
          
          <Typography sx={InvoiceDetailsPopupStyles.retainerSubDescription}>
            המערכת תבדוק אוטומטית הזמנות נוספות במהלך הלילה ותעדכן את החשבונית בהתאם.
          </Typography>
        </Box>
      )}
      
      {approvalPackageData ? (
        <ApprovalPackageDetailsLayout 
          data={approvalPackageData} 
          isRetainerInvoice={isRetainerInvoice}
        />
      ) : (
        <Box sx={InvoiceDetailsPopupStyles.noDataBox}>
          <Typography variant="body1" color="text.secondary">
            נתוני השוואה אינם זמינים
          </Typography>
        </Box>
      )}
    </Box>
  );
};

  const renderNotesTab = () => (
    <InvoiceNotesTab
      invoice={invoice}
      onSaveNotes={onSaveNotes}
      refreshFiles={refreshFiles} 
      onNavigateToFile={() => navigateToTab('file', tabsConfig)} 
      onNavigateToSpecificFile={handleNavigateToSpecificFile}
    />
  );

  const renderApprovalTab = () => (
    <InvoiceApprovalActions
      invoice={invoice}
      onApprove={handleApproveInvoice}
      onReject={handleRejectInvoice}
      onTransferApprover={handleTransferApprover}
      isProcessing={isProcessingApproval}
      onNavigateToComparison={() => navigateToTab('comparison', tabsConfig)}
      onNavigateToComments={() => navigateToTab('notes', tabsConfig)}
      initiateApproval={initiateApproval}
      allocationKeyModalOpen={allocationKeyModalOpen}
      setAllocationKeyModalOpen={setAllocationKeyModalOpen}
      allocationKeys={allocationKeys}
      isLoadingAllocationKeys={isLoadingAllocationKeys}
      allocationKeysError={allocationKeysError}
      currentInvoiceYear={currentInvoiceYear}
      completeApprovalWithAllocationKey={completeApprovalWithAllocationKey}
      availableApprovers={approvers}
      isLoadingApprovers={isLoadingApprovers}
      isTransferringFromParent={isTransferring}
      updateInvoiceOptimistically={updateInvoiceOptimistically}
    />
  );

  const renderActivityLogTab = () => (
    <InvoiceActivityLogTab invoice={invoice} />
  );

  const renderFileViewer = () => (
    <Box sx={InvoiceDetailsPopupStyles.fileViewerBox}>
      <FileViewer
        currentFile={currentFile}
        allFiles={allFiles}
        currentFileIndex={currentFileIndex}
        totalFiles={totalFiles}
        hasNextFile={hasNextFile}
        hasPreviousFile={hasPreviousFile}
        onNextFile={goToNextFile}
        onPreviousFile={goToPreviousFile}
        onGoToFile={goToFile}
        isLoadingFiles={isLoadingFiles}
        fileAsBase64={fileAsBase64}
        fileName={invoice?.fileName}
        isLoadingFile={isLoadingFile}
      />
    </Box>
  );

  const hasComparisonData = invoice?.status === 'ממתין לאישור' || 
                           invoice?.status === 'מאושר' || 
                           invoice?.status === 'נדחה' || 
                           invoice?.status === 'REQUIRES_LINE_MAPPING' ||
                           invoice?.status === 'PENDING_RETAINER_ALLOCATION' ||
                           invoice?.status === 'PENDING_CLARIFICATION' ||
                           invoice?.status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
                           (invoice?.status === 'חשבונית להמשך טיפול' && approvalPackageData) ||
                           invoice?.approvedBy || 
                           invoice?.rejectedBy ||
                           invoice?.isRetainerInvoice ||
                           !!invoice?.errorDetails; 
  
  const canShowApprovalTab = invoice?.status === 'ממתין לאישור' || invoice?.status === 'REQUIRES_LINE_MAPPING' || invoice?.status === 'PENDING_CLARIFICATION';
  
  const tabsConfig = [
    { id: 'details', label: 'פרטי חשבונית', component: renderInvoiceDetails },
    ...(hasComparisonData ? [
       { id: 'comparison', label: 'השוואת פרטי הזמנה וחשבונית', component: renderComparisonTab }
    ] : []),
    ...(canShowApprovalTab ? [
      { id: 'approval', label: 'אישור החשבונית', component: renderApprovalTab }
    ] : []),
    { id: 'notes', label: 'הערות', component: renderNotesTab },
    { id: 'file', label: `קבצים וצרופות${totalFiles > 1 ? ` (${totalFiles})` : ''}`, component: renderFileViewer },
    { id: 'activityLog', label: 'יומן פעילות', component: renderActivityLogTab }
  ];
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: InvoiceDetailsPopupStyles.paperProps
      }}
    >
      <DialogTitle sx={InvoiceDetailsPopupStyles.dialogTitle(theme)}>
        <Typography variant="h6" fontWeight="bold">
          {invoice ? `חשבונית ${invoice.booknum || invoice.id}${invoice.supplierName ? ` - ${invoice.supplierName}` : ''}` : 'פרטי חשבונית'}
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={InvoiceDetailsPopupStyles.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={InvoiceDetailsPopupStyles.tabs(theme)}
        >
          {tabsConfig.map((tab, index) => (
            <Tab key={tab.id} label={tab.label} value={index} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={InvoiceDetailsPopupStyles.dialogContent}>
        {tabsConfig[activeTab]?.component()}
      </DialogContent>

      <DialogActions sx={InvoiceDetailsPopupStyles.dialogActions(theme)}>
        <Button onClick={onClose} variant="outlined">
          סגור
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDetailsPopup;